import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

export default function KanbanColumn({ status, title, bugs, onDrop, onDragOver, onViewDetails, isBugFix = false }) {
  const handleDragStart = (e, bug) => {
    e?.dataTransfer?.setData('bugId', bug?.id);
    e?.dataTransfer?.setData('isBugFix', isBugFix?.toString());
  };

  const getStatusColor = () => {
    if (isBugFix) {
      switch (status) {
        case 'open': return 'bg-primary/15 text-foreground';
        case 'in_progress': return 'bg-primary/15 text-primary';
        case 'resolved': return 'bg-success/15 text-success';
        case 'closed': return 'bg-muted text-foreground';
        default: return 'bg-muted text-foreground';
      }
    }
    
    switch (status) {
      case 'open': return 'bg-error/15 text-error';
      case 'in_progress': return 'bg-warning/15 text-warning';
      case 'resolved': return 'bg-success/15 text-success';
      case 'closed': return 'bg-muted text-foreground';
      default: return 'bg-muted text-foreground';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    try {
      // Handle if date is already a Date object
      if (date instanceof Date) {
        return date?.toISOString()?.split('T')?.[0];
      }
      
      // Handle if date is a string
      if (typeof date === 'string') {
        return date?.split('T')?.[0];
      }
      
      // Handle timestamp
      if (typeof date === 'number') {
        return new Date(date)?.toISOString()?.split('T')?.[0];
      }
      
      return 'N/A';
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  return (
    <div
      className="flex-shrink-0 w-80 bg-muted rounded-lg p-4"
      onDrop={(e) => onDrop?.(e, status)}
      onDragOver={onDragOver}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-foreground">{title}</h3>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor()}`}>
            {bugs?.length || 0}
          </span>
          {isBugFix && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary/15 text-foreground">
              Fixes
            </span>
          )}
        </div>
      </div>
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {bugs?.map((bug) => (
          <div
            key={bug?.id}
            draggable
            onDragStart={(e) => handleDragStart(e, bug)}
            onClick={() => onViewDetails?.(bug)}
            className="bg-card rounded-lg p-4 shadow-sm border border-border hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={16} className="text-muted-foreground" />
                <h4 className="font-medium text-foreground">{bug?.title}</h4>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor()}`}>
                {bug?.status}
              </span>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{formatDate(bug?.createdAt)}</span>
              </div>
              
              {bug?.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {bug?.description}
                </p>
              )}
            </div>
          </div>
        ))}
        
        {bugs?.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No {isBugFix ? 'fixes' : 'bugs'} {title?.toLowerCase()}</p>
          </div>
        )}
      </div>
    </div>
  );
}